import { searchVtuberById } from "../api/commonApi";
import { logAction } from "../api/logApi";
import { ACCESSORY_OPTIONS, EYE_COLOR_OPTIONS, GENRE_OPTIONS, HAIR_COLOR_OPTIONS, HAIR_LENGTH_OPTIONS, STREAM_FREQUENCY_OPTIONS, SUBSCRIBER_OPTIONS } from "../data/mockVtubers";
import { mulberry32 } from "./mathUtil";

const STORAGE_KEY = "vtuber-search:userPreference";

const MAX_ATTRIBUTE_ENTRIES = 200;

let hasLoggedInit = false;

const WEIGHT = {
  tagClick: 2,
  searchFilter: 2,
  vtuberView: 1,
  onboarding: 10,
};

const targetAttributes = [
  ...GENRE_OPTIONS.map(({value})=>value),
  ...HAIR_COLOR_OPTIONS.map(({value})=>value),
  ...EYE_COLOR_OPTIONS.map(({value})=>value),
  ...HAIR_LENGTH_OPTIONS.map(({value})=>value),
  ...ACCESSORY_OPTIONS.map(({value})=>value),
  ...STREAM_FREQUENCY_OPTIONS.map(({value})=>value),
  ...SUBSCRIBER_OPTIONS.map(({value})=>value)
]

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      if (!hasLoggedInit) {
        hasLoggedInit = true;
        logAction("user_preference_init", { attributeCount: targetAttributes.length });
      }

      return {
        attributes: Object.fromEntries(targetAttributes.map(v=>[v,0])),
        onboarded:false
      };
    }

    const parsed = JSON.parse(raw);
    return {
      attributes: parsed.attributes ?? {},
      onboarded:parsed.onboarded ?? false
    };
  } catch (error) {
    console.warn("ユーザー傾向の読み込みに失敗しました", error);
    return { attributes: {} };
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.warn("ユーザー傾向の保存に失敗しました", error);
  }
}

function bumpAttribute(store, value, weight) {
  if (!value) {
    return;
  }


  if(targetAttributes.includes(value)){
    store.attributes[value] = (store.attributes[value] ?? 0) + weight;
  }
}

function pruneAttributes(store) {
  const entries = Object.entries(store.attributes);
  if (entries.length <= MAX_ATTRIBUTE_ENTRIES) {
    return;
  }

  entries.sort((a, b) => b[1] - a[1]);
  store.attributes = Object.fromEntries(entries.slice(0, MAX_ATTRIBUTE_ENTRIES));
}

// タグクリックなど、明示的にVTuberの属性へ関心を示した操作を記録する
export function recordTagInterest(tag) {
  const store = readStore();
  bumpAttribute(store, tag, WEIGHT.tagClick);
  pruneAttributes(store);
  writeStore(store);
}

// 検索条件として選ばれた属性を記録する（vtuberNameなどのフリーテキストは除外）
export function recordSearchFilters(filters) {
  const store = readStore();

  Object.entries(filters ?? {}).forEach(([key, value]) => {
    if (key === "vtuberName" || !value) {
      return;
    }

    bumpAttribute(store, value, WEIGHT.searchFilter);
  });

  pruneAttributes(store);
  writeStore(store);
}

// あるVに関心を持った場合にそれが持っているattributesを関心材料とする
export async function recordVtuberView(vtuberId) {
  if (!vtuberId) {
    return;
  }

  const vtuberData = await searchVtuberById(vtuberId)

  
  const store = readStore();
  
  vtuberData.attributes?.forEach((a)=>{
    bumpAttribute(store,a,WEIGHT.vtuberView)
  })
  writeStore(store);
}

// スコアが高い順に属性を返す（レコメンドや初期フィルタ提案などに利用）
export function getTopAttributes(limit = 10) {
  const store = readStore();

  return Object.entries(store.attributes)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([value, score]) => ({ value, score }));
}

export const weightedRandom = (count, beta = 0.1) => {
  const result = [];
  const pool = { ...readStore().attributes };
  const timeSeed = new Date()/(1000*60*5)
  const randomFunc = mulberry32(timeSeed);

  for (let i = 0; i < count; i++) {
    const entries = Object.entries(pool);

    if (entries.length === 0) {
      break;
    }

    // 分配関数 Z
    const weights = entries.map(([key, value]) => [
      key,
      Math.exp(beta * value),
    ]);

    const partitionFunction = weights.reduce(
      (sum, [, weight]) => sum + weight,
      0
    );

    // 確率分布
    let random = randomFunc() * partitionFunction;

    let selectedKey;

    for (const [key, weight] of weights) {
      random -= weight;

      if (random < 0) {
        selectedKey = key;
        break;
      }
    }

    result.push(selectedKey);

    // 重複を許さない
    delete pool[selectedKey];
  }

  return result;
};

// 初回訪問時の好み選択チェックボックスを表示済みかどうか
export function hasCompletedOnboarding() {
  return readStore().onboarded === true;
}

// 初回訪問時に選択された属性へ大きく加点し、以後は選択モーダルを出さないようにする
export function completeOnboarding(values) {
  const store = readStore();

  (values ?? []).forEach((value) => {
    bumpAttribute(store, value, WEIGHT.onboarding);
  });

  store.onboarded = true;
  pruneAttributes(store);
  writeStore(store);
}

export function clearUserPreference() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("ユーザー傾向の削除に失敗しました", error);
  }
}

export function getPreferenceMagnitude(){
  const sum = Object.values(readStore().attributes).reduce((sum, value) => sum + value, 0);
  return sum
}
